// utils/assistantUtil.js
import { TypeClassSession } from "constant/TypeClassSession";

// Constants: Status definitions
export const status = {
  NEW: "NEW",
  NEW_OVERDUE: "NEW_OVERDUE",
  OVERDUE: "OVERDUE",
  IN_PROGRESS: "IN_PROGRESS",
  IN_PROGRESS_OVERDUE: "IN_PROGRESS_OVERDUE",
  COMPLETED_OVERDUE: "COMPLETED_OVERDUE",
  COMPLETED_ON_TIME: "COMPLETED_ON_TIME",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
};

// Constants: Status labels
export const statusLabel = {
  NEW: "New",
  NEW_OVERDUE: "New overdue",
  OVERDUE: "Overdue",
  IN_PROGRESS: "In progress",
  IN_PROGRESS_OVERDUE: "In progress overdue",
  COMPLETED_OVERDUE: "Completed overdue",
  COMPLETED_ON_TIME: "Completed on time",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

// Constants: Color mappings for statuses
export const colorsMap = [
  {
    status: status.NEW,
    label: statusLabel.NEW,
    color: "#EF6F34",
    type: "linear",
    gradient: { from: "#EF6F34", to: "#FFC4A9" },
  },
  {
    status: status.NEW_OVERDUE,
    label: statusLabel.NEW_OVERDUE,
    color: "#8392AB",
    type: "linear",
    gradient: { from: "#EF6F34", to: "#FFC4A9" },
  },
  {
    status: status.OVERDUE,
    label: statusLabel.OVERDUE,
    color: "#FF8800",
    type: "linear",
    gradient: { from: "#F54A45", to: "#FF8800" },
  },
  {
    status: status.IN_PROGRESS,
    label: statusLabel.IN_PROGRESS,
    color: "#11CDEF",
    type: "linear",
    gradient: { from: "#1171EF", to: "#11CDEF" },
  },
  {
    status: status.IN_PROGRESS_OVERDUE,
    label: statusLabel.IN_PROGRESS_OVERDUE,
    color: "#2DCE89",
    type: "linear",
    gradient: { from: "#176845", to: "#2DCE89" },
  },
  {
    status: status.COMPLETED_OVERDUE,
    label: statusLabel.COMPLETED_OVERDUE,
    color: "#F5365C",
    type: "linear",
    gradient: { from: "#F5365C", to: "#F07680" },
  },
  {
    status: status.COMPLETED_ON_TIME,
    label: statusLabel.COMPLETED_ON_TIME,
    color: "#DF25FD",
    type: "linear",
    gradient: { from: "#DF25FD", to: "#D483E2" },
  },
  {
    status: status.COMPLETED,
    label: statusLabel.COMPLETED,
    color: "#43E97B",
    type: "linear",
    gradient: { from: "#43E97B", to: "#059279" },
  },
  {
    status: status.CANCELLED,
    label: statusLabel.CANCELLED,
    color: "#D2D2D2",
    type: "linear",
    gradient: { from: "#ECE9E6", to: "#D2D2D2" },
  },
];

// Constants: Tab definitions for todos
export const tabTodos = ["All", "Report", "Feedback", "Other"];

// Utils: Group progress and gradebook reports by class name
export const groupReport = (progressReport, gradeBookReport) => {
  const _progressReport = [];
  const _gradebookReport = [];

  (progressReport.data || []).forEach((item) => {
    const className = item.className;
    const progressReportEventIndex = _progressReport.findIndex(
      (item) => item.className === className
    );
    if (progressReportEventIndex === -1) {
      _progressReport.push({ ...item, items: [item] });
    } else {
      _progressReport[progressReportEventIndex].items = [
        ..._progressReport[progressReportEventIndex].items,
        item,
      ];
    }
  });

  (gradeBookReport.data || []).forEach((item) => {
    const className = item.className;
    const gradeBookReportIndex = _gradebookReport.findIndex(
      (item) => item.className === className
    );
    if (gradeBookReportIndex === -1) {
      _gradebookReport.push({ ...item, items: [item] });
    } else {
      _gradebookReport[gradeBookReportIndex].items = [
        ..._gradebookReport[gradeBookReportIndex].items,
        item,
      ];
    }
  });

  return { _gradebookReport, _progressReport };
};

// Utils: Capitalize the first letter of a string
export const capitalizeFirstLetter = (string) => {
  if (string === "NOT_COMPLETED") return "To do";
  return (
    string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  ).replace(/_/g, " ");
};

// Utils: Calculate label for pie chart (finished/total)
export const calculateLabel = (items) => {
  const total = (items ?? []).reduce(
    (sum, item) => (typeof item.number === "number" ? sum + item.number : sum),
    0
  );
  const finished = (items ?? [])
    .filter((item) => capitalizeFirstLetter(item.status).includes("Completed"))
    .reduce(
      (sum, item) =>
        typeof item.number === "number" ? sum + item.number : sum,
      0
    );
  return `${finished}/${total}`;
};

// Utils: Get item ID based on type
export const getItemId = (item, itemType) => {
  switch (itemType) {
    case "Feedback":
      return item?.feedback?.feedBackId;
    default:
      return item?.id;
  }
};

// Utils: Get item deadline based on type
export const getItemDeadline = (item, itemType) => {
  switch (itemType) {
    case "Feedback":
      return item?.feedback?.deadlineDate;
    case TypeClassSession.STUDENTS_AT_RISK:
      return item?.endTime;
    default:
      return item?.deadlineEndTime;
  }
};

// Utils: Get item title based on type
export const getItemTitle = (item, itemType) => {
  switch (itemType) {
    case "Feedback":
      return item?.feedback?.feedBackSubject;
    case TypeClassSession.STUDENTS_AT_RISK:
      return `${item?.studentName}${item.nickName ? ` (${item.nickName})` : ""}`;
    default:
      return item?.className ?? "";
  }
};

// Utils: Get item status based on type
export const getItemStatus = (item, itemType) => {
  if (itemType === TypeClassSession.STUDENTS_AT_RISK) {
    return capitalizeFirstLetter(item?.continuousStatus);
  } else if (["Daily Report", "Progress Report"].includes(itemType)) {
    return capitalizeFirstLetter(item?.status);
  } else {
    return capitalizeFirstLetter(item.status);
  }
};

// Utils: Transform reporting data for pie chart
export const getReportingPieChart = (data) => {
  return (data?.data?.reporting ?? []).map((item) => {
    const colorItem = colorsMap.find(
      (color) => status[item.status] === color.status
    );
    return {
      id: item.status.toLowerCase(),
      label:
        capitalizeFirstLetter(item.status) === "Not complete"
          ? "In progress"
          : capitalizeFirstLetter(item.status),
      value: item.number,
      percent: item.percent,
      color: colorItem?.color ?? "black",
    };
  });
};

// Utils: Transform planning data for pie chart
export const getPlanningPieChart = (data) => {
  return (data?.data?.planning ?? []).map((item) => {
    const colorItem = colorsMap.find(
      (color) => status[item.status] === color.status
    );
    return {
      id: item.status.toLowerCase(),
      label: capitalizeFirstLetter(item.status),
      value: item.number,
      percent: item.percent,
      color: colorItem?.color ?? "black",
    };
  });
};

// Utils: Get overdue count for reporting
export const getReportingOverdue = (data) => {
  return (
    (data?.data?.reporting ?? []).find(
      (item) => capitalizeFirstLetter(item.status) === "In progress overdue"
    )?.number ?? 0
  );
};

// Utils: Get overdue count for planning
export const getPlanningOverdue = (data) => {
  return (
    (data?.data?.planning ?? []).find(
      (item) => capitalizeFirstLetter(item.status) === "In progress overdue"
    )?.number ?? 0
  );
};

// Utils: Get tasks to complete today for reporting
export const getReportingOnCompleteToday = (data) => {
  return (data?.data?.reporting ?? [])
    .filter((item) =>
      ["New", "In progress"].includes(capitalizeFirstLetter(item.status))
    )
    .reduce(
      (sum, item) =>
        typeof item.number === "number" ? sum + item.number : sum,
      0
    );
};

// Utils: Get tasks to complete today for planning
export const getPlanningOnCompleteToday = (data) => {
  return (data?.data?.planning ?? [])
    .filter((item) =>
      ["New", "In progress"].includes(capitalizeFirstLetter(item.status))
    )
    .reduce(
      (sum, item) =>
        typeof item.number === "number" ? sum + item.number : sum,
      0
    );
};

// Utils: Sort task list by deadline
export const sortList = (list) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDeadlineStatus = (item) => {
    const deadline = new Date(
      item?.task?.deadlineDate ?? item?.feedback?.deadlineDate
    );
    deadline.setHours(0, 0, 0, 0);

    if (deadline < today) {
      return "passed";
    } else if (deadline.getTime() === today.getTime()) {
      return "today";
    } else {
      return "upcoming";
    }
  };

  return list.sort((a, b) => {
    const [statusA, dateA] = [
      getDeadlineStatus(a),
      new Date(a?.task?.deadlineDate ?? a?.feedback?.deadlineDate).getTime(),
    ];
    const [statusB, dateB] = [
      getDeadlineStatus(b),
      new Date(b?.task?.deadlineDate ?? b?.feedback?.deadlineDate).getTime(),
    ];

    if (statusA !== statusB) {
      return statusA === "passed" ? -1 : 1;
    }
    return dateB - dateA;
  });
};

export const parseScheduleTimes = (rawValue) => {
  if (typeof rawValue !== "string" || rawValue.trim() === "") {
    return [];
  }

  try {
    const fixed = rawValue
      .replace(/'/g, '"')
      .replace(/\bTrue\b/g, "true")
      .replace(/\bFalse\b/g, "false");

    const data = JSON.parse(fixed);

    if (!Array.isArray(data)) {
      return [];
    }

    // lọc các item không đúng cấu trúc
    return data.filter((item) => {
      return (
        typeof item.id === "number" &&
        typeof item.name === "string" &&
        Array.isArray(item.time_slots)
      );
    });
  } catch (e) {
    console.error("Cannot parse scheduleTimes:", e);
    return [];
  }
};
